/**
 * DrawingChecks — list page for CR-2026-DC-FINAL-CHECK (D-032 §5.2 + §15.2).
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import StandardsCallout from "../../components/StandardsCallout";
import { resultLabel } from "../../utils/drawingChecksApi";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded } from "@mui/icons-material";

import { listChecks, CHECK_RESULTS } from "../../utils/drawingChecksApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const RESULT_COLOR = {
  IN_PROGRESS: { bg: "#fff3cd", color: "#664d03" },
  ACCEPTED:    { bg: "#d1e7dd", color: "#0a3622" },
  REJECTED:    { bg: "#f8d7da", color: "#58151c" },
  AMENDED:     { bg: "#fd7e14", color: "#ffffff" },
};

export default function DrawingChecks() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const documentIdParam = params.get("document_id") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [resultFilter, setResultFilter] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = {};
    if (documentIdParam) filters.document_id = documentIdParam;
    if (resultFilter && resultFilter !== "ALL") filters.result = resultFilter;
    listChecks(filters)
      .then((data) => { if (!cancelled) setRows((data && data.rows) || []); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [documentIdParam, resultFilter]);

  return (
    <>
      <Helmet><title>Drawing Final Checks | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <Typography color="text.primary">Drawing Final Checks</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>Drawing Final Checks</Typography>
            <Chip label="Beta" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
          </Stack>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate(documentIdParam ? `/drawing-checks/new?document_id=${documentIdParam}` : "/drawing-checks/new")}>
            New Final Check
          </Button>
        </Stack>
        <PageGuide
          why="Catch drawing errors before they reach the shop — so you never fabricate from a bad drawing."
          who="Checkers and the QM, catching drawing errors before fabrication."
          what="A 7-area final check (geometry, connections, material, welding, coating, erection, completeness) recorded against a drawing and revision."
          how="Start a check, work the 7-area checklist, clear any critical findings, then Accept or Reject."
          when="Before a drawing is released for fabrication — it must be Accepted here first."
          standard="AISC Drawing Control Ch. 7/8/9 §5.2"
        />
        <StandardsCallout sx={{ mb: 2 }}>
          <strong>AISC Drawing Control Ch. 7/8/9 §5.2</strong> — the final drawing check. A drawing must be
          <strong>Accepted</strong> here before it can be released for fabrication.
        </StandardsCallout>

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select label="Result" size="small" sx={{ minWidth: 200 }}
            value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
            <MenuItem value="ALL">All results</MenuItem>
            {CHECK_RESULTS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
          This feature isn't available yet — please contact your administrator.
        </Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="drawing-checks" lockedColumns={["Check No.", "Drawing → Rev", "Result"]}>
            <TableHead><TableRow>
              <TableCell>Check No.</TableCell><TableCell>Drawing → Rev</TableCell><TableCell>Checked By</TableCell>
              <TableCell>Checklist</TableCell><TableCell>Result</TableCell><TableCell>Started</TableCell>
              <TableCell>Accepted</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6c757d" }}>
                  No final checks. Click <strong>New Final Check</strong> to start one.
                </TableCell></TableRow>
              )}
              {!loading && rows.map((dc) => {
                const c = RESULT_COLOR[dc.result] || { bg: "#e9ecef", color: "#495057" };
                return (
                  <TableRow key={dc.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/drawing-checks/${dc.id}`)}>
                    <TableCell><strong>#{dc.id}</strong></TableCell>
                    <TableCell>Drawing {dc.document_id} → {dc.drawing_revision}</TableCell>
                    <TableCell>User {dc.checker_user_id}</TableCell>
                    <TableCell>{dc.checklist_version}</TableCell>
                    <TableCell><Chip size="small" label={resultLabel(dc.result)} sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: "0.7rem" }} /></TableCell>
                    <TableCell>{dc.started_at ? new Date(dc.started_at).toISOString().slice(0, 10) : "—"}</TableCell>
                    <TableCell>{dc.accepted_at ? new Date(dc.accepted_at).toISOString().slice(0, 10) : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
